"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import Modal from "@modules/common/components/modal";
import LocalizedClientLink from "@modules/common/components/localized-client-link";
import X from "@modules/common/icons/x";
import { MagnifyingGlass } from "@medusajs/icons";
import { API_BASE_URL, STORE_BASE_CURRENCY } from "@lib/config";
import { getSearchContext } from "@lib/search/session";
import { sendSearchClick, sendSearchEvents } from "@lib/search/events";
import { saveSearchAttribution } from "@lib/search/attribution";
import { getClientAuthHeaders } from "@lib/auth/client-token";
import MoneyClient from "../../../components/MoneyClient";
import { normalizeCurrency, toCents } from "@lib/currency";
import { normalizeSelectableCountryCode, resolveClientCountrySelection } from "@lib/storefront-selection";
import { capitalizeFirstLetter } from "@lib/util/text";
import { resolveProductHref } from "@lib/util/product-route";
type SearchBarProps = {
  isOpen: boolean;
  onClose: () => void;
};
type ProductHit = {
  _id: string;
  name: string;
  slug: string;
  price: number;
  currency?: string;
  discountedPrice?: number | null;
  images?: Array<{
    url?: string;
  } | string>;
  thumbnail?: string;
  videoUrl?: string | null;
  brand?: {
    slug?: string | null;
    name?: string | null;
  };
};
type FacetEntry = {
  value: string;
  count?: number | null;
};
type SuggestionEntry = {
  query: string;
  count?: number | null;
};
type AutocompleteResponse = {
  query?: string;
  suggestions?: SuggestionEntry[];
  products?: ProductHit[];
  items?: ProductHit[];
  brands?: FacetEntry[];
  categories?: FacetEntry[];
  collections?: FacetEntry[];
  recentSearches?: string[];
  trendingSearches?: string[];
  queryId?: string | null;
};
const SEARCH_DEBOUNCE_MS = 250;
const RESULTS_LIMIT = 10;
const RECOMMENDED_FETCH_LIMIT = 24;
const RECOMMENDED_DISPLAY_LIMIT = 8;
const shuffleArray = <T,>(source: T[]): T[] => {
  const array = [...source];
  for (let index = array.length - 1; index > 0; index--) {
    const swapWith = Math.floor(Math.random() * (index + 1));
    [array[index], array[swapWith]] = [array[swapWith], array[index]];
  }
  return array;
};
const resolveProductImage = (product: ProductHit) => {
  if (product.thumbnail) {
    return product.thumbnail;
  }
  const firstImage = Array.isArray(product.images) ? product.images[0] : undefined;
  if (!firstImage) {
    return null;
  }
  if (typeof firstImage === "string") {
    return firstImage;
  }
  return firstImage?.url ?? null;
};
const hasRenderableProductImage = (product: ProductHit) => Boolean(resolveProductImage(product));
const toFacetLabel = (entry: FacetEntry) => entry.count ? `${entry.value} (${entry.count})` : entry.value;
const buildInternalAutocompletePayload = (value: string, payload: AutocompleteResponse): AutocompleteResponse => {
  const products = Array.isArray(payload?.products) ? payload.products : Array.isArray(payload?.items) ? payload.items : [];
  const brandCounts = new Map<string, number>();
  products.forEach(product => {
    const brand = product.brand?.name || product.brand?.slug || "";
    const normalized = brand.trim();
    if (!normalized) return;
    brandCounts.set(normalized, (brandCounts.get(normalized) || 0) + 1);
  });
  return {
    query: value,
    suggestions: value ? products.slice(0, 5).map(product => ({
      query: product.name
    })) : [],
    products,
    brands: Array.from(brandCounts.entries()).map(([brand, count]) => ({
      value: brand,
      count
    })),
    categories: [],
    collections: [],
    recentSearches: [],
    trendingSearches: []
  };
};
export default function SearchBar({
  isOpen,
  onClose
}: SearchBarProps) {
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [data, setData] = useState<AutocompleteResponse | null>(null);
  const [emptyData, setEmptyData] = useState<AutocompleteResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [recommendedProducts, setRecommendedProducts] = useState<ProductHit[]>([]);
  const [isLoadingRecommendations, setIsLoadingRecommendations] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>(() => resolveClientCountrySelection());
  const inputRef = useRef<HTMLInputElement | null>(null);
  const abortController = useRef<AbortController | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestQueryRef = useRef("");
  const closeAndReset = useCallback(() => {
    setQuery("");
    setData(null);
    setError(null);
    setIsLoading(false);
    setHasSearched(false);
    setRecommendedProducts([]);
    onClose();
  }, [onClose]);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const focusTimeout = setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => {
      clearTimeout(focusTimeout);
    };
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeAndReset();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [closeAndReset, isOpen]);
  useEffect(() => {
    const handler = (event_0: Event) => {
      const detail = (event_0 as CustomEvent<{
        country?: string;
      }>).detail;
      const nextCountry = normalizeSelectableCountryCode(detail?.country);
      if (!nextCountry) return;
      setSelectedCountry(nextCountry);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("lealti:country", handler as EventListener);
      return () => window.removeEventListener("lealti:country", handler as EventListener);
    }
    return undefined;
  }, []);
  useEffect(() => {
    setData(null);
    setEmptyData(null);
    setRecommendedProducts([]);
  }, [selectedCountry]);
  const fetchAutocomplete = useCallback(async (value: string, {
    storeEmpty
  }: {
    storeEmpty?: boolean;
  } = {}) => {
    if (abortController.current) {
      abortController.current.abort();
    }
    const controller = new AbortController();
    abortController.current = controller;
    latestQueryRef.current = value;
    setIsLoading(true);
    setError(null);
    try {
      const normalizedValue = value.trim().slice(0, 80);
      if (!normalizedValue) {
        const emptyPayload = buildInternalAutocompletePayload("", {});
        if (storeEmpty) {
          setEmptyData(emptyPayload);
        } else {
          setData(emptyPayload);
        }
        setHasSearched(true);
        return;
      }
      const url = new URL(`${API_BASE_URL}/public/products`);
      url.searchParams.set("q", normalizedValue);
      url.searchParams.set("limit", String(RESULTS_LIMIT));
      url.searchParams.set("countryCode", selectedCountry);
      const response = await fetch(url.toString(), {
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
          ...getClientAuthHeaders()
        }
      });
      if (!response.ok) {
        throw new Error(`Search failed with status ${response.status}`);
      }
      const payload: AutocompleteResponse = await response.json();
      if (latestQueryRef.current !== value) {
        return;
      }
      const internalPayload = buildInternalAutocompletePayload(normalizedValue, payload);
      if (storeEmpty) {
        setEmptyData(internalPayload);
      } else {
        setData(internalPayload);
      }
      setHasSearched(true);
    } catch (err) {
      if ((err as any)?.name === "AbortError") {
        return;
      }
      console.error("Search request failed", err);
      setError("We couldn't complete your search. Please try again.");
      if (!storeEmpty) {
        setData(null);
      }
      setHasSearched(true);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCountry]);
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const trimmed = query.trim();
    if (!trimmed) {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setData(null);
      setEmptyData(null);
      setIsLoading(false);
      setError(null);
      return;
    }
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      void fetchAutocomplete(trimmed);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [fetchAutocomplete, isOpen, query]);
  useEffect(() => {
    if (!isOpen) {
      if (abortController.current) {
        abortController.current.abort();
        abortController.current = null;
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    }
  }, [isOpen]);
  const fetchRecommendedProducts = useCallback(async () => {
    if (isLoadingRecommendations) {
      return;
    }
    setIsLoadingRecommendations(true);
    try {
      const url_0 = new URL(`${API_BASE_URL}/public/products`);
      url_0.searchParams.set("limit", String(RECOMMENDED_FETCH_LIMIT));
      url_0.searchParams.set("countryCode", selectedCountry);
      const response_0 = await fetch(url_0.toString(), {
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response_0.ok) {
        throw new Error(`Recommendations failed with status ${response_0.status}`);
      }
      const payload_0 = await response_0.json();
      const items = Array.isArray(payload_0?.products) ? payload_0.products as ProductHit[] : [];
      const randomized = shuffleArray(items.filter(hasRenderableProductImage)).slice(0, RECOMMENDED_DISPLAY_LIMIT);
      setRecommendedProducts(randomized);
    } catch (err_0) {
      console.error("Recommended products request failed", err_0);
      setRecommendedProducts([]);
    } finally {
      setIsLoadingRecommendations(false);
    }
  }, [isLoadingRecommendations, selectedCountry]);
  useEffect(() => {
    if (!isOpen || !hasSearched || isLoading) {
      return;
    }
    const products = data?.products ?? [];
    if (products.length > 0 || recommendedProducts.length > 0) {
      return;
    }
    if (query.trim()) {
      fetchRecommendedProducts();
    }
  }, [data?.products, fetchRecommendedProducts, hasSearched, isLoading, isOpen, query, recommendedProducts.length]);
  const displayData = query.trim() ? data : emptyData;
  const suggestions = displayData?.suggestions ?? [];
  const recentSearches = displayData?.recentSearches ?? [];
  const trendingSearches = displayData?.trendingSearches ?? [];
  const products_0 = displayData?.products ?? [];
  const categories = displayData?.categories ?? [];
  const brands = displayData?.brands ?? [];
  const collections = displayData?.collections ?? [];
  const handleSearchSubmit = useCallback(() => {
    const trimmed_0 = query.trim();
    if (!trimmed_0) return;
    router.push(`/search?q=${encodeURIComponent(trimmed_0)}`);
    closeAndReset();
  }, [closeAndReset, query, router]);
  const handleSuggestionClick = useCallback(async (suggestion: string, suggestionType: string, position: number) => {
    const trimmed_1 = query.trim();
    const {
      sessionId
    } = getSearchContext();
    await sendSearchEvents({
      suggestions: [{
        sessionId,
        query: trimmed_1,
        suggestion,
        suggestionType,
        position
      }]
    });
    setQuery(suggestion);
    router.push(`/search?q=${encodeURIComponent(suggestion)}`);
    closeAndReset();
  }, [closeAndReset, query, router]);
  const handleProductClick = useCallback(async (product: ProductHit, position_0: number) => {
    const trimmed_2 = query.trim();
    const {
      sessionId: sessionId_0,
      userToken,
      platform
    } = getSearchContext();
    const queryId = displayData?.queryId || null;
    await sendSearchClick({
      sessionId: sessionId_0,
      userToken,
      platform,
      query: trimmed_2,
      queryId,
      productId: product._id,
      position: position_0,
      pageContext: "search_modal"
    });
    saveSearchAttribution({
      sessionId: sessionId_0,
      userToken,
      platform,
      query: trimmed_2,
      queryId: queryId || undefined,
      productId: product._id,
      productSlug: product.slug,
      position: position_0,
      pageContext: "search_modal"
    });
    closeAndReset();
  }, [closeAndReset, displayData?.queryId, query]);
  const renderProductGrid = (items_0: ProductHit[]) => <div className="grid grid-cols-2 gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {items_0.map((product_0, index) => {
      const href = resolveProductHref(product_0);
      const price = typeof product_0.discountedPrice === "number" && product_0.discountedPrice > 0 ? product_0.discountedPrice : product_0.price;
      const imageUrl = resolveProductImage(product_0);
      const videoUrl = !imageUrl ? product_0.videoUrl ?? null : null;
      const displayProductName = capitalizeFirstLetter(product_0.name);
      return <LocalizedClientLink key={product_0._id} href={href} className="group flex flex-col" onClick={() => void handleProductClick(product_0, index + 1)}>
            <div className="relative overflow-hidden rounded-lg bg-gray-100 transition group-hover:shadow-md aspect-[3/4]">
              {imageUrl ? <Image src={imageUrl} alt={displayProductName || product_0.name} fill sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 20vw" className="object-cover" /> : videoUrl ? <video className="absolute inset-0 h-full w-full object-cover" src={videoUrl} muted playsInline autoPlay loop /> : <div className="absolute inset-0 flex items-center justify-center text-xs text-gray-400">
                  No image
                </div>}
            </div>
            <div className="mt-4 text-sm font-lealti-light text-gray-900">
              {displayProductName || product_0.name}
            </div>
            <div className="mt-1 text-sm font-lealti-light text-gray-600">
              <MoneyClient amountCents={toCents(price)} fromCurrency={normalizeCurrency(product_0.currency || STORE_BASE_CURRENCY, STORE_BASE_CURRENCY)} />
            </div>
          </LocalizedClientLink>;
    })}
    </div>;
  const renderFacetList = (title: string, entries: FacetEntry[], type: string) => {
    if (!entries.length) return null;
    return <div className="mt-4">
        <p className="text-sm font-lealti-light text-gray-500">{title}</p>
        <ul className="mt-2 flex flex-wrap gap-3">
          {entries.map((entry, index_0) => <li key={`${type}-${entry.value}`}>
              <button type="button" onClick={() => void handleSuggestionClick(entry.value, type, index_0 + 1)} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:border-gray-400">
                {toFacetLabel(entry)}
              </button>
            </li>)}
        </ul>
      </div>;
  };
  const renderContent = () => {
    if (!query.trim() && !isLoading) {
      return <div className="mt-6 flex-1 overflow-y-auto pb-6">
          {recentSearches.length > 0 && <div>
              <p className="text-sm font-lealti-light text-gray-500">
                Búsquedas recientes
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {recentSearches.map((entry_0, index_1) => <button key={`recent-${entry_0}`} type="button" onClick={() => void handleSuggestionClick(entry_0, "recent", index_1 + 1)} className="rounded-none border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:border-gray-400">
                    {entry_0}
                  </button>)}
              </div>
            </div>}

          {trendingSearches.length > 0 && <div className="mt-6">
              <p className="text-sm font-lealti-light text-gray-500">
                Tendencias
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {trendingSearches.map((entry_1, index_2) => <button key={`trending-${entry_1}`} type="button" onClick={() => void handleSuggestionClick(entry_1, "query", index_2 + 1)} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:border-gray-400">
                    {entry_1}
                  </button>)}
              </div>
            </div>}

          {!recentSearches.length && !trendingSearches.length && <div className="flex items-center justify-center text-sm text-gray-500 font-lealti-light">
              Escribe para buscar productos
            </div>}
        </div>;
    }
    if (isLoading) {
      return <div className="flex flex-1 items-center justify-center text-sm text-gray-500 font-lealti-light">
          Buscando productos...
        </div>;
    }
    if (error) {
      return <div className="flex flex-1 items-center justify-center text-sm text-gray-500 text-center px-4 font-lealti-light">
          {error}
        </div>;
    }
    if (hasSearched && products_0.length === 0) {
      return <div className="mt-6 flex-1 overflow-y-auto pb-6">
          <div className="flex items-center justify-center px-4 text-center text-sm text-gray-500 font-lealti-light">
            No se encontraron resultados para “{query.trim()}”.
          </div>
          {(isLoadingRecommendations || recommendedProducts.length > 0) && <div className="mt-8">
              <p className="text-sm font-lealti-light text-gray-900 sm:text-base">
                Productos recomendados
              </p>
              {isLoadingRecommendations ? <div className="mt-4 flex items-center justify-center text-sm font-lealti-light text-gray-500">
                  Cargando productos recomendados...
                </div> : <div className="mt-4">
                  {renderProductGrid(recommendedProducts)}
                </div>}
            </div>}
        </div>;
    }
    return <div className="mt-6 flex-1 overflow-y-auto pb-6">
        {recentSearches.length > 0 && <div className="mb-4">
            <p className="text-sm font-lealti-light text-gray-500">
              Búsquedas recientes
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {recentSearches.map((entry_2, index_3) => <button key={`recent-${entry_2}`} type="button" onClick={() => void handleSuggestionClick(entry_2, "recent", index_3 + 1)} className="rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 hover:border-gray-400">
                  {entry_2}
                </button>)}
            </div>
          </div>}

        {suggestions.length > 0 && <div className="mb-6">
            <p className="text-sm font-lealti-light text-gray-500">
              Sugerencias
            </p>
            <ul className="mt-2 flex flex-col gap-1">
              {suggestions.map((suggestion_0, index_4) => <li key={`suggestion-${suggestion_0.query}`}>
                  <button type="button" onClick={() => void handleSuggestionClick(suggestion_0.query, "query", index_4 + 1)} className="w-full text-left text-base text-gray-900 transition hover:text-gray-500 font-lealti-light">
                    {suggestion_0.query}
                  </button>
                </li>)}
            </ul>
          </div>}

        {products_0.length > 0 && <div>
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm font-lealti-light text-gray-500">
                Productos
              </p>
              <button type="button" onClick={handleSearchSubmit} className="text-xs font-lealti-light text-gray-600 underline">
                Ver todos
              </button>
            </div>
            {renderProductGrid(products_0)}
          </div>}

        {renderFacetList("Categorías", categories, "category")}
        {renderFacetList("Marcas", brands, "brand")}
        {renderFacetList("Colecciones", collections, "collection")}
      </div>;
  };
  return <Modal isOpen={isOpen} close={closeAndReset} size="full" search data-testid="search-modal">
      <div className="flex h-full w-full flex-col rounded-none bg-white px-4 pb-6 pt-6 shadow-none sm:mx-auto sm:h-[70vh] sm:max-w-5xl sm:rounded-3xl sm:px-10 lg:px-12">
        <div className="flex items-center gap-3 border-b border-gray-200 pb-4">
          <MagnifyingGlass className="h-6 w-6 flex-shrink-0 text-gray-500" />
          <input ref={inputRef} type="search" value={query} onChange={event_1 => setQuery(event_1.target.value)} placeholder="Buscar" aria-label="Buscar productos" onKeyDown={event_2 => {
          if (event_2.key === "Enter") {
            event_2.preventDefault();
            handleSearchSubmit();
          }
        }} className="h-10 flex-1 border-0 bg-transparent text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0 sm:text-lg font-lealti-light" />
          <button type="button" onClick={closeAndReset} aria-label="Close search" className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 transition hover:bg-gray-100 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        {renderContent()}
      </div>
    </Modal>;
}